import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { SecondaryButton } from "./DesignSystemButtons";
import { useExcelDownload } from "./useExcelDownload";
import { fetchTable, assertListingResponse } from "hooks/useTableQuery";
import { downloadTableFile } from "services/utils/downloadTableFile";
import FileDownload from "@mui/icons-material/FileDownload";
import { filterToolbarButtonSx } from "./filterToolbarStyles";

export function exportUrl(endpoint, query, format, columns) {
  const params = new URLSearchParams(query);
  params.delete("page"); params.delete("page_size");
  params.set("export_format", format);
  if (columns?.length) params.set("columns", columns.join(","));
  return `${endpoint}?${params}`;
}

// Report writers need every matching row, not just the displayed page.
// Use the listing contract so Excel/CSV retain the same report layout as PDF.
export async function downloadListingReport({ endpoint, query, queryAliases, headers, mapRow = row => row, filename, format, signal, writers, metadata = {}, filterLabels = {}, sortOptions = [], onProgress }) {
  const params = new URLSearchParams(query);
  params.delete("export_format"); params.delete("columns");
  params.set("page_size", "100");
  const rows = [];
  const seen = new Set();
  let page = 1, totalPages = 1, response;
  do {
    params.set("page", String(page));
    response = await fetchTable(`${endpoint}?${params}`, { signal });
    if (signal?.aborted) return;
    assertListingResponse(response, params.toString(), queryAliases);
    const pagination = response.pagination;
    if (pagination.page !== page || !Number.isInteger(pagination.total_pages) || pagination.total_pages < 0) {
      throw new Error("Could not load the complete report. Please try again.");
    }
    if (page > 1 && totalPages !== pagination.total_pages) {
      throw new Error("Events changed during export. Please download the report again.");
    }
    totalPages = pagination.total_pages;
    if (page < totalPages && !response.results.length) throw new Error("Could not load the complete report. Please try again.");
    for (const row of response.results) {
      if (row.id != null) {
        if (seen.has(row.id)) throw new Error("Events changed during export. Please download the report again.");
        seen.add(row.id);
      }
      rows.push(row);
    }
    if (format === "pdf" && (rows.length > 5000 || (!pagination.count_is_estimate && pagination.total_count > 5000))) {
      throw new Error("PDF supports up to 5,000 rows. Use Excel or CSV.");
    }
    onProgress?.(Math.round(page / Math.max(totalPages, 1) * 40));
    page += 1;
  } while (page <= totalPages);
  if (signal?.aborted) return;
  if (!rows.length) throw new Error("No events match your filters.");
  if (!response.pagination.count_is_estimate && response.pagination.total_count !== rows.length) {
    throw new Error("Events changed during export. Please download the report again.");
  }
  const state = Object.fromEntries(new URLSearchParams(query));
  const filters = Object.fromEntries(Object.entries(state)
    .filter(([key]) => !["page", "page_size", "sort_by", "sort_order"].includes(key))
    .map(([key, value]) => [filterLabels[queryAliases?.[key] || key] || key, value]));
  const sortKey = state.sort_by || response.sort?.sort_by || "event_time";
  const sorts = [{ label: sortOptions.find(option => option.value === sortKey)?.label || sortKey,
    direction: state.sort_order || response.sort?.sort_order || "desc" }];
  const details = {
    ...metadata,
    "Report Name": filename,
    "Report Generated On": new Date().toLocaleString("en-GB"),
    "Overall Total Count": rows.length,
    ...(response.query.defaults_applied.includes("event_time") && !state.event_time_from && !state.event_time_to && state.event_time !== "all"
      ? { "Date Range": "Last 7 days (default)" } : {}),
  };
  const writer = format === "pdf" ? writers.downloadPdf : format === "csv" ? writers.downloadCsv : writers.downloadExcel;
  return writer(rows.map(mapRow), headers.filter(header => header.key), filename, filters, details, sorts,
    progress => onProgress?.(40 + Math.round(progress * 0.6)));
}

export async function downloadStandardExport({ endpoint, reportEndpoint, query, headers, mapRow, filename = "report", format, signal, writers, ...reportOptions }) {
  const columns = headers.filter(header => header.key).map(header => header.key);
  if (!columns.length) throw new Error("Choose at least one data column to export.");
  if (reportEndpoint) return downloadListingReport({ ...reportOptions, endpoint: reportEndpoint, query, headers, mapRow, filename, format, signal, writers });
  const url = exportUrl(endpoint, query, format, columns);
  if (format !== "pdf") {
    return downloadTableFile(url, { signal, filename: `${filename}.${format === "excel" ? "xlsx" : "csv"}`, format });
  }
  const body = await fetchTable(url, { signal });
  if (signal?.aborted) return;
  if (!Array.isArray(body.results)) throw new Error("Could not load the export. Please try again.");
  if (!body.results.length) throw new Error("No events match your filters.");
  if (body.results.length > 5000) throw new Error("PDF supports up to 5,000 rows. Use Excel or CSV.");
  return writers.downloadPdf(body.results.map(mapRow), headers.filter(header => header.key), filename);
}

export function StandardExportButton({ endpoint, query, headers, mapRow, filename = "report", customToolbar = false }) {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(null);
  const writers = useExcelDownload({ maxPdfRows: 5000 });
  useEffect(() => () => request.current?.abort(), []);
  const download = async format => {
    setAnchor(null);
    if (request.current) return;
    const controller = new AbortController(); request.current = controller;
    setBusy(true); setError("");
    try {
      await downloadStandardExport({ endpoint, query, headers, mapRow, filename, format, signal: controller.signal, writers });
    } catch (e) { if (!controller.signal.aborted && e.name !== "AbortError") setError(e.message || "Could not export records."); }
    finally { if (!controller.signal.aborted) { request.current = null; setBusy(false); } }
  };
  return <React.Fragment>
    <SecondaryButton aria-haspopup="menu" aria-expanded={Boolean(anchor)} disabled={busy}
      startIcon={customToolbar ? <FileDownload /> : undefined} sx={customToolbar ? filterToolbarButtonSx : undefined}
      onClick={e => setAnchor(e.currentTarget)}>{busy ? "Exporting…" : customToolbar ? "Download" : "Export"}</SecondaryButton>
    <Mui.Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
      {[['excel', 'Excel'], ['csv', 'CSV'], ['pdf', 'PDF']].map(([format, label]) => <Mui.MenuItem key={format} onClick={() => download(format)}>{label}</Mui.MenuItem>)}
    </Mui.Menu>
    {error && <Mui.Alert severity="error" onClose={() => setError("")}>{error}</Mui.Alert>}
  </React.Fragment>;
}
